import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, Sparkles, Save, Trash2, BookmarkCheck } from "lucide-react";

/**
 * Iter-203 · Universal Column Mapper
 * Users upload any CSV (Zoho / Tally / Vyapar / Marg / QuickBooks / hand-made)
 * and map source columns → Zenith targets. Auto-suggested mapping is
 * pre-filled based on 60+ aliases; user overrides any suggestion before
 * commit. Works for customer, vendor, and voucher imports.
 */

const ENTITIES = [
  { v: "customer", l: "Customer · Party Master" },
  { v: "vendor", l: "Vendor · Party Master" },
  { v: "voucher", l: "Voucher · Journal Entries" },
];

function parseCsv(text) {
  const rows = [];
  let cur = [], cell = "", inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i], n = text[i + 1];
    if (inQ) {
      if (c === '"' && n === '"') { cell += '"'; i++; }
      else if (c === '"') { inQ = false; }
      else { cell += c; }
    } else {
      if (c === '"') { inQ = true; }
      else if (c === ",") { cur.push(cell); cell = ""; }
      else if (c === "\n" || c === "\r") {
        if (c === "\r" && n === "\n") i++;
        cur.push(cell); rows.push(cur); cur = []; cell = "";
      } else { cell += c; }
    }
  }
  if (cell.length || cur.length) { cur.push(cell); rows.push(cur); }
  const nonEmpty = rows.filter(r => r.some(v => (v || "").trim().length));
  if (!nonEmpty.length) return { headers: [], rows: [] };
  const [headerRow, ...body] = nonEmpty;
  const headers = headerRow.map(h => h.trim());
  const dataRows = body.map(r => Object.fromEntries(headers.map((h, i) => [h, (r[i] || "").trim()])));
  return { headers, rows: dataRows };
}

const CONFIDENCE_TONE = {
  high: "text-emerald-600 border-emerald-500/50 bg-emerald-50",
  medium: "text-amber-600 border-amber-500/50 bg-amber-50",
  low: "text-orange-600 border-orange-500/50 bg-orange-50",
};

export default function MigratorMapper() {
  const [step, setStep] = useState(1);
  const [entity, setEntity] = useState("customer");
  // Iter-213 · "By Voucher Type" upload flow
  // Iter-218 · User-friendly upload-style selector — the first question a
  // user should answer when picking a voucher CSV. Two styles:
  //   • "gl"       → GL / Accounting Entries — one row per Dr/Cr line
  //                  (Tally daybook, Zoho manual journals, ledger extract).
  //                  Maps to uploadMode=mixed OR by_kind+multi.
  //   • "register" → Register / Document List — one row per whole document
  //                  (Vyapar sales register, Marg invoice list, bank
  //                  statement). Maps to uploadMode=by_kind + row_model=single
  //                  with a specific voucher kind (Sales / Purchase / Bank / …).
  const [uploadStyle, setUploadStyle] = useState("gl");
  const [uploadMode, setUploadMode] = useState("mixed");   // "mixed" | "by_kind"
  const [voucherKind, setVoucherKind] = useState("sales");
  const [rowModel, setRowModel] = useState("multi");        // "multi" | "single"
  const [voucherKinds, setVoucherKinds] = useState([]);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState({ headers: [], rows: [] });
  const [schema, setSchema] = useState(null);
  const [mapping, setMapping] = useState({});         // source_col → target_key
  const [confidence, setConfidence] = useState({});   // source_col → high|medium
  const [valueInferredCols, setValueInferredCols] = useState([]);
  const [missingRequired, setMissingRequired] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Iter-213 · Load voucher kinds once so the selector is populated.
  useEffect(() => {
    api.get("/migrator/mapper/voucher-kinds")
       .then((r) => setVoucherKinds(r.data || []))
       .catch(() => {});
  }, []);

  // Load schema whenever entity / voucher_kind changes. Passing
  // ?voucher_kind=<kind> returns only the fields relevant to that kind.
  useEffect(() => {
    (async () => {
      try {
        const useKind = entity === "voucher" && uploadMode === "by_kind" && voucherKind;
        const url = `/migrator/mapper/target-schema/${entity}` +
                     (useKind ? `?voucher_kind=${voucherKind}` : "");
        const r = await api.get(url);
        setSchema(r.data);
      } catch (e) { toast.error("Failed to load target schema"); }
    })();
  }, [entity, uploadMode, voucherKind]);

  // Keep row_model in sync with the kind's allowed models.
  useEffect(() => {
    if (uploadMode !== "by_kind") return;
    const k = voucherKinds.find((v) => v.key === voucherKind);
    if (!k) return;
    if (!k.row_models.includes(rowModel)) setRowModel(k.row_models[0] || "multi");
  }, [voucherKind, voucherKinds, uploadMode, rowModel]);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => {
      const p = parseCsv(String(reader.result || ""));
      setParsed(p);
      if (!p.headers.length) { toast.error("No headers detected"); return; }
      toast.success(`Parsed ${p.headers.length} columns · ${p.rows.length} rows`);

      // Iter-219 · Smart Detection — guess GL vs Register from header
      // signatures so the user only has to confirm. Signals:
      //   • has_dr_cr        → GL (Tally daybook / Zoho manual JV)
      //   • has_gst          → Register (Vyapar / Marg / bank statement)
      //   • has_invoice_ref  → Register (bank receipt / payment)
      //
      // Iter-221 · If the user is on the default Customer entity but the
      // CSV is clearly a voucher file, we also auto-switch entity so the
      // GL / Register toggle appears without a manual click.
      {
        const norm = (s) => (s || "").toLowerCase().replace(/[^a-z]/g, "");
        const hs = p.headers.map(norm);
        const has = (needles) => needles.some((n) => hs.some((h) => h.includes(n)));
        const has_dr_cr    = has(["debit", "dr", "dramount"]) && has(["credit", "cr", "cramount"]);
        const has_gst      = has(["cgst", "sgst", "igst", "taxable", "taxamt"]);
        const has_inv_ref  = has(["invoiceno", "invoicenumber", "billno", "billnumber",
                                     "againstinvoice", "againstbill", "invoiceref"]);
        const has_receipt  = has(["receiptno", "voucherno", "banktxn", "utr"]);
        const has_cheque   = has(["chequeno", "chequenumber"]);
        let guess = null, reason = "";
        if (has_gst && !has_dr_cr) {
          guess = "register";
          const kindGuess = has_cheque || has_receipt
            ? (has(["payment", "paid", "outward", "withdraw"]) ? "bank_payment" : "bank_receipt")
            : (has(["credit note", "creditnote", "salesreturn"]) ? "credit_note"
              : has(["debit note", "debitnote", "purchasereturn"]) ? "debit_note"
              : has(["purchase", "bill"]) ? "purchase" : "sales");
          reason = `Detected GST columns (${["CGST","SGST","IGST","Taxable"].filter((x) => hs.some((h) => h.includes(x.toLowerCase()))).join(", ")})`;
          setUploadStyle("register");
          setUploadMode("by_kind");
          setRowModel("single");
          setVoucherKind(kindGuess);
        } else if (has_dr_cr) {
          guess = "gl";
          reason = "Detected Debit + Credit columns";
          setUploadStyle("gl");
          setUploadMode("mixed");
          setRowModel("multi");
        } else if (has_inv_ref && has_receipt) {
          guess = "register";
          reason = "Detected Invoice-ref + Voucher-No columns (bank register)";
          setUploadStyle("register");
          setUploadMode("by_kind");
          setRowModel("single");
          setVoucherKind("bank_receipt");
        }
        if (guess) {
          toast.info(`Zenith guessed this is a ${guess.toUpperCase()} file — ${reason}. Change the toggle below if wrong.`, { duration: 6000 });
          // Iter-221 · If the user landed on the mapper with the default
          // Customer entity but the file is clearly a voucher file,
          // auto-switch so the GL / Register toggle appears without a
          // manual click.
          if (entity !== "voucher") setEntity("voucher");
        }
      }

      // Iter-219 · Mapping Draft — restore any saved in-progress mapping
      // for the same file (matched by header signature). Skips
      // customer/vendor because the signature isn't reliable there.
      try {
        const key = "mm-draft:" + entity + ":" + p.headers.join("|").slice(0, 200);
        const raw = localStorage.getItem(key);
        if (raw) {
          const draft = JSON.parse(raw);
          if (draft && draft.mapping) {
            toast(`Restored an in-progress mapping (${Object.keys(draft.mapping).length} columns saved earlier). Undo →`, {
              action: {
                label: "Discard",
                onClick: () => { localStorage.removeItem(key); setMapping({}); },
              },
              duration: 10000,
            });
            if (draft.mapping)     setMapping(draft.mapping);
            if (draft.uploadStyle) setUploadStyle(draft.uploadStyle);
            if (draft.voucherKind) setVoucherKind(draft.voucherKind);
            if (draft.rowModel)    setRowModel(draft.rowModel);
          }
        }
      } catch { /* localStorage disabled */ }
    };
    reader.readAsText(f);
  };

  // Iter-219 · Auto-save mapping draft to localStorage on every change
  // so a page reload doesn't wipe minutes of work. Keyed by the CSV's
  // header signature so drafts don't cross-pollute across files.
  useEffect(() => {
    if (!parsed.headers.length || !Object.keys(mapping).length) return;
    try {
      const key = "mm-draft:" + entity + ":" + parsed.headers.join("|").slice(0, 200);
      localStorage.setItem(key, JSON.stringify({
        mapping, uploadStyle, voucherKind, rowModel,
        _savedAt: new Date().toISOString(),
      }));
    } catch { /* localStorage disabled */ }
  }, [mapping, entity, parsed.headers, uploadStyle, voucherKind, rowModel]);

  const analyze = async () => {
    if (!parsed.headers.length) return toast.error("Upload a CSV first");
    setBusy(true);
    try {
      const r = await api.post("/migrator/mapper/analyze", {
        entity, headers: parsed.headers,
        sample_rows: parsed.rows.slice(0, 5),
      });
      setMapping(r.data.map || {});
      setConfidence(r.data.confidence || {});
      setValueInferredCols(r.data.value_inferred_columns || []);
      setMissingRequired(r.data.missing_required || []);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyze failed");
    }
    setBusy(false);
  };

  const setMap = (src, tgt) => {
    const next = { ...mapping };
    if (tgt === "__none__") delete next[src];
    else {
      // Iter-217 · A single "mapping" row now covers BOTH standard fields
      // (date, debit, account_name, …) AND parent Dimensions (encoded as
      // `dim:<parent_id>`). Uniqueness rule: two columns can't map to the
      // SAME standard target (a debit column mapped twice makes no sense);
      // but two columns can share a Dimension parent — since a "Branch"
      // parent can accept values from multiple raw columns only via
      // separate rows, drop that dedupe when target starts with `dim:`.
      if (!tgt.startsWith("dim:")) {
        for (const k of Object.keys(next)) if (next[k] === tgt) delete next[k];
      }
      next[src] = tgt;
    }
    setMapping(next);
    // Recompute missing required (Dimensions are never required)
    const required = (schema?.fields || []).filter(f => f.required).map(f => f.key);
    const mappedTargets = new Set(Object.values(next).filter((v) => !v.startsWith("dim:")));
    setMissingRequired(required.filter(k => !mappedTargets.has(k)));
  };

  /** Iter-217 · Split the unified `mapping` back into what the backend
   *  expects: `column_map` (standard targets) and `dimension_map`
   *  (source_col → parent_dim_id). */
  const _splitMapping = () => {
    const column_map = {};
    const dimension_map = {};
    for (const [src, tgt] of Object.entries(mapping)) {
      if (!tgt) continue;
      if (tgt.startsWith("dim:")) dimension_map[src] = tgt.slice(4);
      else column_map[src] = tgt;
    }
    return { column_map, dimension_map };
  };

  const preview_ = async () => {
    if (missingRequired.length) return toast.error(`Map required columns first: ${missingRequired.join(", ")}`);
    setBusy(true);
    try {
      const isKindFlow = entity === "voucher" && uploadMode === "by_kind";
      const { column_map, dimension_map } = _splitMapping();
      const r = await api.post("/migrator/mapper/import", {
        entity, column_map, rows: parsed.rows, dry_run: true,
        dimension_map: entity === "voucher" ? dimension_map : undefined,
        voucher_kind: isKindFlow ? voucherKind : undefined,
        row_model: isKindFlow ? rowModel : undefined,
      });
      setPreview(r.data);
      setStep(3);
      // Iter-217 · Surface the "why" if nothing will import so the user
      // isn't left staring at a Commit button that doesn't work.
      if (!r.data.would_import) {
        toast.warning(
          r.data.errors?.length
            ? `0 rows will import — every row is missing required fields (${r.data.errors[0].missing?.join(', ') || 'see below'}). Go back and map the required columns.`
            : "0 rows will import — check that your CSV has data rows below the header.",
          { duration: 8000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
    setBusy(false);
  };

  const commit = async () => {
    // Iter-217 · Skip the native confirm() dialog for voucher imports
    // (users reported the browser dialog was being missed / dismissed).
    // Keep the confirm ONLY for customer/vendor imports where the code
    // mode choice is critical.
    if (entity === "customer" || entity === "vendor") {
      const codeMsg = codeMode === "auto"
        ? `Auto-generate codes starting from ${codePrefix}${String(codeStart).padStart(4,"0")}?`
        : codeMode === "from_column"
          ? "Read code from your CSV's mapped 'Customer / Vendor Code' column — proceed?"
          : "Leave code blank on every party — proceed?";
      if (!confirm(`Import ${preview?.would_import || 0} ${entity}(s)?\n\n` +
                     `${entity === "vendor" ? "Vendor" : "Customer"} Code mode: ${codeMode.toUpperCase()}\n${codeMsg}\n\nContinue?`)) return;
    }
    setBusy(true);
    try {
      const isKindFlow = entity === "voucher" && uploadMode === "by_kind";
      const { column_map, dimension_map } = _splitMapping();
      const r = await api.post("/migrator/mapper/import", {
        entity, column_map, rows: parsed.rows, dry_run: false,
        customer_code_mode: codeMode,
        customer_code_prefix: codePrefix,
        customer_code_start: parseInt(codeStart) || 1,
        dimension_map: entity === "voucher" ? dimension_map : undefined,
        approve_subdims: entity === "voucher" ? approveSubdims : undefined,
        voucher_kind: isKindFlow ? voucherKind : undefined,
        row_model: isKindFlow ? rowModel : undefined,
      });
      setResult(r.data);
      toast.success(`Created ${r.data.created}${r.data.updated ? ` · updated ${r.data.updated}` : ""}`);
      if (r.data.auto_saved_preset) {
        toast.info(`Mapping auto-saved as preset "${r.data.auto_saved_preset.name}" — reload it next time.`);
        loadPresets();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed");
    }
    setBusy(false);
  };

  // Iter-204 · customer/vendor code state
  const [codeMode, setCodeMode] = useState("blank");     // 'auto' | 'from_column' | 'blank'
  // Iter-220 · Default code prefix auto-flips to "VEND" when the Mapper
  // entity is a vendor so bulk-imported vendor codes don't start with
  // "CUST". Users can still override.
  const [codePrefix, setCodePrefix] = useState("CUST");
  useEffect(() => {
    setCodePrefix(entity === "vendor" ? "VEND" : "CUST");
  }, [entity]);
  const [codeStart, setCodeStart] = useState("1");

  // Iter-204 · GSTIN verification state
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifications, setVerifications] = useState(null);
  const [accepted, setAccepted] = useState(new Set());   // row_index of rows the user accepted portal-suggested values for

  // Iter-205 · Mapping presets
  const [presets, setPresets] = useState([]);
  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [presetVendor, setPresetVendor] = useState("custom");

  // Iter-209/217 · Dimension parents list (rendered inline in the unified
  // mapping dropdown). `dimensionMap` state removed — the merged `mapping`
  // now carries `dim:<parent_id>` values, and `_splitMapping()` peels
  // them off at submit time.
  const [parents, setParents] = useState([]);
  const [approveSubdims, setApproveSubdims] = useState(true);

  const loadParents = React.useCallback(async () => {
    try {
      const r = await api.get("/dimensions", { params: { parents_only: true } });
      setParents(r.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadParents(); }, [loadParents, entity]);

  const loadPresets = useCallback(async () => {
    try {
      const r = await api.get("/migrator/mapper/presets", { params: { entity } });
      setPresets(r.data || []);
    } catch { /* ignore */ }
  }, [entity]);
  useEffect(() => { loadPresets(); }, [entity, loadPresets]);

  const applyPreset = async (p) => {
    setMapping(p.column_map || {});
    // Confidence isn't stored — mark every applied mapping as "high" so
    // the UI reflects the user's own approval from last time.
    const conf = {};
    Object.keys(p.column_map || {}).forEach((k) => { conf[k] = "high"; });
    setConfidence(conf);
    const required = (schema?.fields || []).filter((f) => f.required).map((f) => f.key);
    const mappedTargets = new Set(Object.values(p.column_map || {}));
    setMissingRequired(required.filter((k) => !mappedTargets.has(k)));
    try { await api.post(`/migrator/mapper/presets/${p.id}/use`); loadPresets(); } catch { /* ignore */ }
    toast.success(`Applied preset "${p.name}"`);
  };

  const savePreset = async () => {
    if (!presetName.trim()) return toast.error("Preset name required");
    try {
      const r = await api.post("/migrator/mapper/presets", {
        entity, name: presetName.trim(),
        source_vendor: presetVendor.trim().toLowerCase() || "custom",
        column_map: mapping,
      });
      toast.success(r.data.status === "updated" ? "Preset updated" : "Preset saved");
      setSavePresetOpen(false); setPresetName("");
      loadPresets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deletePreset = async (pid) => {
    if (!confirm("Delete this preset permanently?")) return;
    try { await api.delete(`/migrator/mapper/presets/${pid}`); toast.success("Preset deleted"); loadPresets(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  useEffect(() => {
    setCodePrefix(entity === "vendor" ? "VEND" : "CUST");
  }, [entity]);

  const verifyGstins = async () => {
    if (!preview?.preview?.length) return;
    setVerifyBusy(true);
    try {
      const r = await api.post("/migrator/mapper/verify-gstins", {
        rows: preview.preview.map((p) => ({
          name: p.name, gstin: p.gstin, pan: p.pan,
          state: p.state, state_code: p.state_code,
          legal_name: p.legal_name,
        })),
      });
      setVerifications(r.data);
      toast.success(`${r.data.summary.ok} OK · ${r.data.summary.flagged} flagged · ${r.data.summary.invalid} invalid · ${r.data.summary.skipped} skipped`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "GSTIN verification on gst.gov.in failed");
    }
    setVerifyBusy(false);
  };

  const acceptAllOk = () => {
    if (!verifications) return;
    const ok = new Set(accepted);
    verifications.verifications.forEach((v) => { if (v.status === "ok") ok.add(v.row_index); });
    setAccepted(ok);
    toast.success(`Accepted ${ok.size} portal-verified row(s)`);
  };
  const toggleAccept = (idx) => {
    const n = new Set(accepted);
    n.has(idx) ? n.delete(idx) : n.add(idx);
    setAccepted(n);
  };

  const reset = () => {
    setStep(1); setFile(null); setParsed({ headers: [], rows: [] });
    setMapping({}); setConfidence({}); setMissingRequired([]);
    setPreview(null); setResult(null);
  };

  const targetOptions = useMemo(() => {
    if (!schema) return [];
    return schema.fields.map(f => ({ v: f.key, l: f.label, req: !!f.required }));
  }, [schema]);

  /** Iter-217 · Unified dropdown options: standard schema targets + every
   *  parent Dimension, all in one list. Prefixes dimension IDs with
   *  `dim:` so the frontend can distinguish them at submit time. */
  const unifiedTargetGroups = useMemo(() => {
    const stdFields = targetOptions;
    const dimFields = (parents || []).map((p) => ({
      v: `dim:${p.id}`,
      l: `Dimension · ${p.name}${p.is_system ? " (system)" : ""}`,
      req: false,
    }));
    return { stdFields, dimFields };
  }, [targetOptions, parents]);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="migrator-mapper-page">
      <PageHeader
        eyebrow="Migration Tools"
        title="Universal Column Mapper"
        description="Upload any CSV (Zoho · Tally · Vyapar · Marg · QuickBooks · SAP · your own). Zenith auto-detects columns and you confirm the mapping before commit. Works for Customer, Vendor and Voucher imports."
      />

      {/* Stepper */}
      <div className="card-sharp p-3 flex items-center gap-3 flex-wrap" data-testid="mm-stepper">
        {[
          { n: 1, l: "Upload CSV" },
          { n: 2, l: "Map columns" },
          { n: 3, l: "Preview & commit" },
        ].map(s => (
          <div key={s.n} className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border-2 ${step === s.n ? "border-accent bg-accent/10" : step > s.n ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-muted-foreground/20 text-muted-foreground"}`} data-testid={`mm-step-${s.n}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step === s.n ? "bg-accent text-white" : step > s.n ? "bg-emerald-500 text-white" : "bg-muted"}`}>{step > s.n ? "✓" : s.n}</div>
            <span className="text-sm font-medium">{s.l}</span>
          </div>
        ))}
      </div>

      {/* ---------------- STEP 1 · Upload ---------------- */}
      {step === 1 && (
        <Card className="p-6 rounded-sm space-y-4" data-testid="mm-step-1-panel">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>What are you importing?</Label>
              <Select value={entity} onValueChange={setEntity}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="mm-entity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ENTITIES.map(e => <SelectItem key={e.v} value={e.v}>{e.l}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-muted-foreground mt-1">
                Zenith reads {schema?.fields?.length || 0} target fields for this entity — {(schema?.fields || []).filter(f => f.required).length} required.
              </div>
            </div>
            <div>
              <Label>Source CSV</Label>
              <div className="flex items-center gap-2 mt-1">
                <label htmlFor="mm-file-inp" className="flex-1 flex items-center gap-2 h-10 px-3 rounded-sm border border-dashed cursor-pointer hover:bg-muted/40">
                  <Upload className="w-4 h-4" />
                  <span className="text-sm truncate">{file ? file.name : "Choose a CSV file"}</span>
                </label>
                <input id="mm-file-inp" type="file" accept=".csv,text/csv" onChange={onFileChange} className="hidden" data-testid="mm-file" />
              </div>
              {parsed.headers.length > 0 && (
                <div className="text-[11px] text-emerald-600 mt-1">
                  Detected: {parsed.headers.length} columns · {parsed.rows.length} rows
                </div>
              )}
            </div>
          </div>

          {/* Iter-218 · Upload-style question (voucher only) — kept as
              the FIRST decision so users don't have to know Zenith's
              internal `uploadMode`/`row_model` vocabulary. */}
          {entity === "voucher" && (
            <div className="rounded-sm border-2 border-l-4 border-l-purple-500 bg-purple-50/40 p-4 space-y-3" data-testid="mm-kind-flow">
              <div className="text-[10px] font-mono uppercase tracking-widest text-purple-700">
                What kind of file is this?
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className={`flex items-start gap-3 p-4 rounded-sm border-2 cursor-pointer ${uploadStyle === "gl" ? "border-purple-500 bg-white shadow-sm" : "border-muted bg-background/50"}`} data-testid="mm-style-gl">
                  <input type="radio" name="mm-upload-style" checked={uploadStyle === "gl"} onChange={() => {
                    setUploadStyle("gl");
                    setUploadMode("mixed");
                    setRowModel("multi");
                  }} className="mt-1" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">GL / Accounting Entries</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      One row per Dr/Cr line, grouped by Voucher No.<br/>
                      <span className="italic">e.g. Tally daybook export, Zoho manual journals, ledger extract.</span>
                    </div>
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-4 rounded-sm border-2 cursor-pointer ${uploadStyle === "register" ? "border-purple-500 bg-white shadow-sm" : "border-muted bg-background/50"}`} data-testid="mm-style-register">
                  <input type="radio" name="mm-upload-style" checked={uploadStyle === "register"} onChange={() => {
                    setUploadStyle("register");
                    setUploadMode("by_kind");
                    setRowModel("single");
                    // Default to Sales register unless user has picked a
                    // register-capable kind already.
                    const kindOK = voucherKinds.find((k) => k.key === voucherKind);
                    if (!kindOK || !(kindOK.row_models || []).includes("single")) {
                      setVoucherKind("sales");
                    }
                  }} className="mt-1" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">Register · one row per document</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      One row per invoice / bill / receipt / payment with GST columns.<br/>
                      <span className="italic">e.g. Vyapar Sales Register, Marg invoice list, bank statement CSV.</span>
                    </div>
                  </div>
                </label>
              </div>

              {uploadStyle === "register" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t">
                  <div>
                    <Label>Which register?</Label>
                    <Select value={voucherKind} onValueChange={setVoucherKind}>
                      <SelectTrigger className="rounded-sm mt-1" data-testid="mm-vkind"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {voucherKinds
                          .filter((k) => (k.row_models || []).includes("single"))
                          .map((k) => (
                            <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <div className="text-[11px] text-muted-foreground mt-1">
                      Zenith will post each row as a <b>{voucherKinds.find((k) => k.key === voucherKind)?.vtype || "?"}</b> voucher, auto-splitting taxable + GST + total into balanced Dr/Cr lines.
                    </div>
                    {/* Iter-219 · Downloadable blank CSV template */}
                    <div className="mt-2">
                      <a
                        href={`${API}/migrator/mapper/register-template/${voucherKind}.csv`}
                        target="_blank" rel="noreferrer"
                        className="text-[11px] font-mono uppercase text-purple-700 hover:underline"
                        data-testid="mm-download-template"
                      >
                        ↓ Download blank {voucherKind.replace(/_/g, " ")} template (.csv)
                      </a>
                      <span className="text-[11px] text-muted-foreground ml-2">— open in Excel, fill in rows, drop back here.</span>
                    </div>
                  </div>
                  <div className="text-[11px] text-muted-foreground bg-white/60 rounded-sm p-2 border">
                    <b>How Zenith splits a Register row:</b><br/>
                    Sales/CN → Party <b>Dr</b> · Sales <b>Cr</b> · Output-GST <b>Cr</b><br/>
                    Purchase/DN → Party <b>Cr</b> · Purchase <b>Dr</b> · Input-GST <b>Dr</b><br/>
                    Bank/Cash Receipt → Bank/Cash <b>Dr</b> · Party or Ledger <b>Cr</b><br/>
                    {["bank_receipt", "bank_payment", "cash_receipt", "cash_payment", "credit_note", "debit_note"].includes(voucherKind) && (
                      <div className="mt-1 text-purple-700"><b>Invoice knocking:</b> map an <b>Invoice / Bill #</b> column to line-tag each row against a specific open invoice → AR/AP reports show which invoice was paid.</div>
                    )}
                  </div>
                </div>
              )}
              {uploadStyle === "gl" && voucherKinds.length > 0 && (
                <div className="text-[11px] text-muted-foreground pt-2 border-t">
                  Your file will be imported as-is — Zenith reads a <code>Voucher Type</code> column per row (Sales, Purchase, Journal, Receipt, Payment). Want tailored columns for a specific kind instead? Pick <b>Register</b> above.
                </div>
              )}
            </div>
          )}

          {parsed.headers.length > 0 && (
            <div className="rounded-sm border p-3 bg-muted/20">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Source columns detected</div>
              <div className="flex flex-wrap gap-1.5">
                {parsed.headers.map(h => (
                  <span key={h} className="text-[11px] rounded-sm border px-2 py-0.5 bg-background font-mono">{h}</span>
                ))}
              </div>
              <div className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Auto-mapper reads header <b>names</b>, then falls back to sample <b>values</b> (GSTIN / PAN / email / IFSC / phone / pincode / date patterns) so unnamed columns still resolve.
              </div>
            </div>
          )}

          {/* Iter-205 · Load a saved preset */}
          {presets.length > 0 && (
            <div className="rounded-sm border p-3 bg-emerald-50/40 border-emerald-500/40" data-testid="mm-presets-panel">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 flex items-center gap-1">
                    <BookmarkCheck className="w-3.5 h-3.5" /> Your saved presets for {entity}
                  </div>
                  <div className="text-[11px] text-muted-foreground">Click one to auto-load its column mapping — saves you remapping every month.</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {presets.map((p) => (
                  <div key={p.id} className="rounded-sm border bg-background px-2 py-1 flex items-center gap-2" data-testid={`mm-preset-${p.name.replace(/\s+/g, "_")}`}>
                    <button type="button" onClick={() => applyPreset(p)} className="text-sm text-emerald-700 hover:underline flex items-center gap-1">
                      <BookmarkCheck className="w-3.5 h-3.5" /> {p.name}
                      <span className="text-[10px] text-muted-foreground ml-1 font-mono">· {p.source_vendor} · used {p.use_count || 0}×</span>
                    </button>
                    <button type="button" onClick={() => deletePreset(p.id)} className="text-red-500 hover:text-red-700" data-testid={`mm-preset-del-${p.name.replace(/\s+/g, "_")}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={analyze} disabled={!parsed.headers.length || busy} data-testid="mm-analyze-btn" className="gap-1.5">
              <Sparkles className="w-4 h-4" /> {busy ? "Analysing…" : "Auto-map columns"} <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </Card>
      )}

      {/* ---------------- STEP 2 · Map ---------------- */}
      {step === 2 && (
        <div className="space-y-4" data-testid="mm-step-2-panel">
          {missingRequired.length > 0 && (
            <div className="card-sharp p-3 border-l-4 border-l-red-500 bg-red-50/50 flex items-start gap-2" data-testid="mm-missing-required">
              <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
              <div className="text-sm">
                <b>Required column(s) not mapped:</b> {missingRequired.map(k => {
                  const f = schema?.fields?.find(x => x.key === k);
                  return f?.label || k;
                }).join(" · ")}. Map them below before continuing.
              </div>
            </div>
          )}
          <div className="card-sharp p-3 border-l-4 border-l-accent bg-accent/5 text-xs">
            <b>Column mapping</b> · left = your file, right = Zenith target (accounting field <b>or</b> a Dimension). Auto-picks are colour-coded; change any dropdown to override. {entity === "voucher" && parents.length > 0 && (
              <span className="text-purple-700"> · <b>{parents.length}</b> Dimension parent(s) available in the dropdown below.</span>
            )}
            {valueInferredCols.length > 0 && (
              <div className="mt-1 text-emerald-700 flex items-center gap-1 flex-wrap">
                <Sparkles className="w-3 h-3" /> <b>{valueInferredCols.length}</b> column(s) matched by inspecting sample values:
                {valueInferredCols.map(c => (
                  <span key={c} className="font-mono bg-emerald-50 border border-emerald-500/40 rounded-sm px-1.5 py-0.5">{c}</span>
                ))}
              </div>
            )}
          </div>
          <div className="card-sharp overflow-hidden">
            <table className="w-full dense">
              <thead>
                <tr>
                  <th className="text-left">Source column</th>
                  <th className="text-left">Sample value</th>
                  <th className="text-left">Auto-confidence</th>
                  <th className="text-left">→ Zenith target · Field or Dimension</th>
                </tr>
              </thead>
              <tbody>
                {parsed.headers.map(h => {
                  const cur = mapping[h] || "";
                  const conf = confidence[h];
                  const sample = parsed.rows[0]?.[h] || "";
                  const isDim = cur.startsWith("dim:");
                  return (
                    <tr key={h} data-testid={`mm-map-row-${h.replace(/\s+/g, "_")}`}
                        className={isDim ? "bg-purple-50/30" : ""}>
                      <td className="font-medium break-words">{h}</td>
                      <td className="text-xs text-muted-foreground font-mono truncate max-w-[200px]">{sample || "—"}</td>
                      <td>
                        {conf ? (
                          <span className={`text-[10px] font-mono uppercase px-2 py-0.5 border rounded-sm ${CONFIDENCE_TONE[conf]}`}>
                            {conf}
                          </span>
                        ) : isDim ? (
                          <span className="text-[10px] font-mono uppercase px-2 py-0.5 border rounded-sm text-purple-700 border-purple-500/40 bg-purple-50">
                            Dimension
                          </span>
                        ) : (
                          <span className="text-[10px] font-mono uppercase text-muted-foreground">unmapped</span>
                        )}
                      </td>
                      <td>
                        <Select value={cur || "__none__"} onValueChange={(v) => setMap(h, v)}>
                          <SelectTrigger className="rounded-sm h-9 min-w-[280px]" data-testid={`mm-target-${h.replace(/\s+/g, "_")}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— skip this column —</SelectItem>
                            <div className="px-2 pt-2 pb-1 text-[10px] font-mono uppercase text-muted-foreground border-b">Accounting field</div>
                            {unifiedTargetGroups.stdFields.map(o => (
                              <SelectItem key={o.v} value={o.v}>
                                {o.l}{o.req ? " *" : ""}
                              </SelectItem>
                            ))}
                            {entity === "voucher" && unifiedTargetGroups.dimFields.length > 0 && (
                              <>
                                <div className="px-2 pt-2 pb-1 text-[10px] font-mono uppercase text-purple-700 border-t border-b">Dimensions · auto-tag every voucher line</div>
                                {unifiedTargetGroups.dimFields.map(o => (
                                  <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                                ))}
                                <div className="px-2 py-1 text-[10px] text-muted-foreground italic border-t">
                                  Add more parents in <b>Settings → Dimensions</b>, then click Refresh below.
                                </div>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <Button variant="outline" onClick={() => setStep(1)} data-testid="mm-back-to-1" className="gap-1.5">
              <ArrowLeft className="w-4 h-4" /> Back
            </Button>
            <Button variant="outline" onClick={() => setSavePresetOpen(true)}
                      disabled={!Object.keys(mapping).length}
                      data-testid="mm-save-preset-btn" className="gap-1.5">
              <Save className="w-4 h-4" /> Save as preset
            </Button>
            {entity === "voucher" && (
              <Button size="sm" variant="ghost" onClick={loadParents}
                      className="h-8 text-[10px] font-mono uppercase gap-1 text-purple-700"
                      data-testid="mm-refresh-dims">
                ↻ Refresh dimensions list
              </Button>
            )}
            <Button onClick={preview_} disabled={busy || missingRequired.length > 0} data-testid="mm-preview-btn" className="gap-1.5 ml-auto">
              {busy ? "Previewing…" : "Preview import"} <ArrowRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Iter-217 · Dimension mapping is now merged into the main column
              mapping table above. This card was intentionally removed. */}

          {/* Save-preset dialog */}
          {savePresetOpen && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSavePresetOpen(false)} data-testid="mm-save-preset-modal">
              <div className="card-sharp bg-background p-4 max-w-md w-full space-y-3" onClick={(e) => e.stopPropagation()}>
                <div className="font-heading text-lg">Save this mapping as a preset</div>
                <div className="text-xs text-muted-foreground">Next time you import a {entity} CSV from the same source, load this preset in one click instead of remapping.</div>
                <div>
                  <Label>Preset name</Label>
                  <Input value={presetName} onChange={(e) => setPresetName(e.target.value)} placeholder="e.g. Zoho Books · Monthly Customer Pull" className="rounded-sm mt-1" data-testid="mm-preset-name-input" />
                </div>
                <div>
                  <Label>Source vendor (optional)</Label>
                  <Select value={presetVendor} onValueChange={setPresetVendor}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="mm-preset-vendor-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="zoho">Zoho Books</SelectItem>
                      <SelectItem value="tally">Tally Prime</SelectItem>
                      <SelectItem value="vyapar">Vyapar</SelectItem>
                      <SelectItem value="marg">Marg</SelectItem>
                      <SelectItem value="busy">Busy</SelectItem>
                      <SelectItem value="quickbooks">QuickBooks</SelectItem>
                      <SelectItem value="sap">SAP</SelectItem>
                      <SelectItem value="custom">Custom / Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setSavePresetOpen(false)}>Cancel</Button>
                  <Button onClick={savePreset} data-testid="mm-preset-save-confirm">Save preset</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ---------------- STEP 3 · Preview & commit ---------------- */}
      {step === 3 && preview && !result && (
        <div className="space-y-4" data-testid="mm-step-3-panel">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="card-sharp p-3">
              <div className="text-[10px] font-mono uppercase text-muted-foreground">Total rows</div>
              <div className="text-2xl font-bold">{parsed.rows.length}</div>
            </div>
            <div className="card-sharp p-3">
              <div className="text-[10px] font-mono uppercase text-muted-foreground">Would import</div>
              <div className="text-2xl font-bold text-emerald-600">{preview.would_import}</div>
            </div>
            <div className="card-sharp p-3">
              <div className="text-[10px] font-mono uppercase text-muted-foreground">Row errors</div>
              <div className={`text-2xl font-bold ${preview.errors?.length ? "text-red-600" : ""}`}>{preview.errors?.length || 0}</div>
            </div>
            <div className="card-sharp p-3">
              <div className="text-[10px] font-mono uppercase text-muted-foreground">Mapped columns</div>
              <div className="text-2xl font-bold text-accent">{Object.keys(mapping).length}</div>
            </div>
          </div>
          {preview.errors?.length > 0 && (
            <div className="card-sharp p-3 border-l-4 border-l-orange-500 bg-orange-50/50">
              <div className="text-sm font-semibold text-orange-700 mb-1">Rows skipped due to missing required fields:</div>
              <ul className="text-xs list-disc pl-5">
                {preview.errors.slice(0, 5).map((e, i) => (
                  <li key={i}>Row {e.row}: missing {e.missing.join(", ")}</li>
                ))}
                {preview.errors.length > 5 && <li>… +{preview.errors.length - 5} more</li>}
              </ul>
            </div>
          )}
          {/* Iter-219 · Register JV Split preview — show the balanced JV
              Zenith will post for the first 3 rows so users can spot
              mis-signs BEFORE clicking Commit. */}
          {preview.jv_preview?.length > 0 && (
            <div className="card-sharp p-4 border-l-4 border-l-emerald-500 space-y-3" data-testid="mm-jv-preview">
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">
                Register → balanced JV preview <span className="normal-case text-muted-foreground">· first {preview.jv_preview.length} row(s) — verify Dr/Cr signs are what you expect before committing</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {preview.jv_preview.map((jv, idx) => (
                  <div key={idx} className="rounded-sm border bg-emerald-50/30 p-2" data-testid={`mm-jv-card-${idx}`}>
                    <div className="text-[11px] flex justify-between items-baseline mb-1">
                      <span><b>{jv.voucher_number}</b> · <span className="text-muted-foreground">{jv.date}</span></span>
                      {jv.balanced
                        ? <span className="text-emerald-700 font-mono">✓ balanced</span>
                        : <span className="text-red-700 font-mono">⚠ off by ₹{(jv.total_debit - jv.total_credit).toFixed(2)}</span>}
                    </div>
                    {jv.party_name && <div className="text-[11px] text-muted-foreground mb-1">Party: <b>{jv.party_name}</b>{jv.invoice_ref ? <> · knocks against <b>{jv.invoice_ref}</b></> : null}</div>}
                    <table className="w-full text-[11px]">
                      <tbody>
                        {jv.lines.map((ln, i) => (
                          <tr key={i} className={ln.synthetic ? "" : "font-mono"}>
                            <td className={`py-0.5 truncate max-w-[140px] ${ln.synthetic ? "italic text-purple-700" : ""}`}>{ln.account_name}{ln.invoice_ref ? <span className="text-[10px] text-emerald-700"> ↪ {ln.invoice_ref}</span> : null}</td>
                            <td className="py-0.5 text-right font-mono">{ln.debit ? fmtINR(ln.debit) : ""}</td>
                            <td className="py-0.5 text-right font-mono">{ln.credit ? fmtINR(ln.credit) : ""}</td>
                          </tr>
                        ))}
                        <tr className="border-t border-emerald-500/40 font-mono font-semibold">
                          <td className="py-0.5">Total</td>
                          <td className="py-0.5 text-right">{fmtINR(jv.total_debit)}</td>
                          <td className="py-0.5 text-right">{fmtINR(jv.total_credit)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-muted-foreground italic">
                Purple italic rows are auto-generated by Zenith from your totals (Party control · GST). Everything else came from your file.
              </div>
            </div>
          )}
          {/* Iter-204 · Auto-enrichments strip */}
          {preview.enrichments_applied?.length > 0 && (
            <div className="card-sharp p-3 border-l-4 border-l-emerald-500 bg-emerald-50/40 text-xs" data-testid="mm-enrichments">
              <b>Zenith auto-enriched your rows:</b>{" "}
              {preview.enrichments_applied.map((e) => ({
                gst_status_normalised: "Zoho GST Treatment → Zenith GST Status",
                pan_from_gstin: "PAN extracted from GSTIN",
                state_code_derived: "State Code derived from GSTIN / state name",
              })[e] || e).join(" · ")}
            </div>
          )}

          {/* Iter-204 · Customer/Vendor code system (only for party imports) */}
          {(entity === "customer" || entity === "vendor") && (
            <div className="card-sharp p-4" data-testid="mm-code-section">
              <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-2">
                {entity === "vendor" ? "Vendor" : "Customer"} Code Generation
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                {[
                  { v: "auto", l: "Auto-generate", d: `e.g. ${codePrefix}${codeStart.padStart(4,"0")}, ${codePrefix}${String(parseInt(codeStart)+1).padStart(4,"0")}…` },
                  { v: "from_column", l: "From source column", d: `Map your CSV's code column to '${entity === "vendor" ? "Vendor" : "Customer"} Code' target above` },
                  { v: "blank", l: "Leave blank", d: "Zenith won't create a code — you can enter later manually" },
                ].map((opt) => (
                  <label key={opt.v} className={`flex items-start gap-2 p-3 rounded-sm border-2 cursor-pointer ${codeMode === opt.v ? "border-accent bg-accent/5" : "border-muted"}`} data-testid={`mm-code-${opt.v}`}>
                    <input type="radio" checked={codeMode === opt.v} onChange={() => setCodeMode(opt.v)} className="mt-1" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{opt.l}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{opt.d}</div>
                    </div>
                  </label>
                ))}
              </div>
              {codeMode === "auto" && (
                <div className="grid grid-cols-2 gap-3 mt-3 max-w-md">
                  <div>
                    <Label className="text-xs">Prefix</Label>
                    <Input value={codePrefix} onChange={(e) => setCodePrefix(e.target.value.toUpperCase())} className="mt-1 rounded-sm font-mono" data-testid="mm-code-prefix" />
                  </div>
                  <div>
                    <Label className="text-xs">Start from</Label>
                    <Input type="number" value={codeStart} onChange={(e) => setCodeStart(e.target.value)} className="mt-1 rounded-sm font-mono" data-testid="mm-code-start" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Iter-204 · GSTIN verify against gst.gov.in */}
          {(entity === "customer" || entity === "vendor") && (
            <div className="card-sharp p-4" data-testid="mm-gstin-verify">
              <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-accent">
                    Verify GSTINs against gst.gov.in
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Cross-checks every GSTIN, flags rows where your data disagrees with the portal. Accept per-row, or bulk-accept all "OK" rows.
                  </div>
                </div>
                <Button size="sm" onClick={verifyGstins} disabled={verifyBusy} data-testid="mm-verify-btn" className="gap-1.5">
                  {verifyBusy ? "Verifying…" : "Verify all GSTINs"}
                </Button>
              </div>
              {verifications && (
                <div>
                  <div className="flex gap-2 flex-wrap text-[11px] mb-2">
                    <span className="rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-500/40 px-2 py-0.5">✓ {verifications.summary.ok} OK</span>
                    <span className="rounded-sm bg-amber-50 text-amber-700 border border-amber-500/40 px-2 py-0.5">⚠ {verifications.summary.flagged} Flagged</span>
                    <span className="rounded-sm bg-red-50 text-red-700 border border-red-500/40 px-2 py-0.5">✗ {verifications.summary.invalid} Invalid</span>
                    <span className="rounded-sm bg-muted text-muted-foreground px-2 py-0.5">— {verifications.summary.skipped} No GSTIN</span>
                    {verifications.summary.errors ? <span className="rounded-sm bg-orange-50 text-orange-700 border border-orange-500/40 px-2 py-0.5">! {verifications.summary.errors} Portal errors</span> : null}
                    <Button size="sm" variant="outline" onClick={acceptAllOk} data-testid="mm-verify-accept-all-ok" className="h-6 text-[11px] py-0 px-2 ml-auto">Accept all OK</Button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full dense">
                      <thead>
                        <tr>
                          <th className="w-8"></th>
                          <th>Name</th>
                          <th>GSTIN</th>
                          <th>Status</th>
                          <th>Mismatches / Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {verifications.verifications.map((v) => (
                          <tr key={v.row_index} data-testid={`mm-verify-row-${v.row_index}`}>
                            <td>
                              {(v.status === "ok" || v.status === "flagged") && (
                                <input type="checkbox" checked={accepted.has(v.row_index)} onChange={() => toggleAccept(v.row_index)} data-testid={`mm-verify-accept-${v.row_index}`} />
                              )}
                            </td>
                            <td className="text-sm">{v.name || "—"}</td>
                            <td className="text-xs font-mono">{v.gstin || "—"}</td>
                            <td>
                              <span className={`text-[10px] font-mono uppercase px-2 py-0.5 border rounded-sm ${
                                v.status === "ok" ? "text-emerald-700 border-emerald-500/40 bg-emerald-50" :
                                v.status === "flagged" ? "text-amber-700 border-amber-500/40 bg-amber-50" :
                                v.status === "invalid" ? "text-red-700 border-red-500/40 bg-red-50" :
                                "text-muted-foreground border-muted"
                              }`}>
                                {v.status}
                              </span>
                            </td>
                            <td className="text-xs">
                              {v.status === "flagged" && v.mismatches?.length > 0 && (
                                <ul className="list-disc pl-4">
                                  {v.mismatches.map((m, i) => (
                                    <li key={i}><b>{m.field}:</b> yours <code>{String(m.yours || "—")}</code> · portal <code>{String(m.portal || "—")}</code></li>
                                  ))}
                                </ul>
                              )}
                              {(v.status === "invalid" || v.status === "error" || v.status === "skipped") && (v.reason || "—")}
                              {v.status === "ok" && <span className="text-emerald-700">All fields match portal — safe to accept</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Iter-209 · Sub-dimensions to be auto-created (voucher only) */}
          {entity === "voucher" && preview.subdim_preview?.length > 0 && (
            <div className="card-sharp p-4 border-l-4 border-l-purple-500 space-y-3" data-testid="mm-subdim-approval">
              <div className="text-[10px] font-mono uppercase tracking-widest text-purple-700 flex items-center gap-2">
                Sub-dimensions to be auto-created
                <label className="text-[11px] normal-case text-foreground flex items-center gap-1 ml-auto">
                  <input type="checkbox" checked={approveSubdims} onChange={(e) => setApproveSubdims(e.target.checked)} data-testid="mm-approve-subdims" />
                  Auto-create these on commit
                </label>
              </div>
              {preview.subdim_preview.map((sd) => (
                <div key={sd.parent_id} className="rounded-sm border p-3 bg-purple-50/40">
                  <div className="text-sm font-medium">
                    <b>{sd.parent_name}</b> <span className="text-muted-foreground text-xs">(from column <code>{sd.source_col}</code>)</span>
                  </div>
                  {sd.new_children?.length > 0 && (
                    <div className="mt-2">
                      <div className="text-[10px] font-mono uppercase text-purple-700">To create ({sd.new_children.length})</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {sd.new_children.map((c) => (
                          <span key={c.name} className="text-[11px] rounded-sm border border-purple-500/40 bg-white px-2 py-0.5 font-mono">
                            {c.name} <span className="text-muted-foreground">×{c.count}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {sd.existing_children_used?.length > 0 && (
                    <div className="mt-2">
                      <div className="text-[10px] font-mono uppercase text-emerald-700">Reusing existing ({sd.existing_children_used.length})</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {sd.existing_children_used.map((c) => (
                          <span key={c.name} className="text-[11px] rounded-sm border border-emerald-500/40 bg-white px-2 py-0.5 font-mono">
                            {c.name} <span className="text-muted-foreground">×{c.count}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {!approveSubdims && (
                <div className="text-xs text-orange-700 bg-orange-50 rounded-sm p-2 border border-orange-200">
                  Auto-create is <b>off</b> — voucher lines using these sub-dimensions will be posted without a tag. You can add the sub-dimensions manually in Settings → Dimensions and re-import.
                </div>
              )}
            </div>
          )}

          <div className="card-sharp overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full dense">
                <thead>
                  <tr>
                    {Object.keys(preview.preview?.[0] || {}).map(k => (
                      <th key={k} className="text-left">{k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(preview.preview || []).map((row, i) => (
                    <tr key={i}>
                      {Object.entries(row).map(([k, v]) => (
                        <td key={k} className="text-xs">{v ?? "—"}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setStep(2)} className="gap-1.5" data-testid="mm-back-to-2">
              <ArrowLeft className="w-4 h-4" /> Back to mapping
            </Button>
            <Button onClick={commit} disabled={busy || !preview.would_import} className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5" data-testid="mm-commit-btn">
              <CheckCircle2 className="w-4 h-4" /> {busy ? "Importing…" : `Commit ${preview.would_import} ${entity}(s)`}
            </Button>
          </div>
        </div>
      )}

      {/* Success */}
      {result && (
        <Card className="p-6 rounded-sm border-l-4 border-l-green-500" data-testid="mm-result">
          <div className="flex items-center gap-2 text-green-600 mb-3">
            <CheckCircle2 className="w-5 h-5" /><span className="font-semibold">Import complete</span>
          </div>
          <div className="text-sm space-y-1">
            <div>Created: <b className="text-emerald-600">{result.created}</b></div>
            {result.updated > 0 && <div>Updated: <b className="text-amber-600">{result.updated}</b></div>}
            {result.errors?.length > 0 && <div>Errors: <b className="text-red-600">{result.errors.length}</b></div>}
            {result.auto_apar_routed > 0 && (
              <div className="mt-2 pt-2 border-t border-border/40 text-xs bg-blue-50/50 rounded-sm p-2">
                <b className="text-blue-700">↳ Auto-routed {result.auto_apar_routed} line(s)</b> with generic "Accounts Payable" / "Accounts Receivable" / "Sundry Debtors" / "Sundry Creditors" captions to your existing system control ledger (no duplicate account created).
              </div>
            )}
            {result.auto_created_subdims?.length > 0 && (
              <div className="mt-2 pt-2 border-t">
                <div className="text-[10px] font-mono uppercase text-purple-700 mb-1">Sub-dimensions auto-created ({result.auto_created_subdims.length})</div>
                <div className="flex flex-wrap gap-1">
                  {result.auto_created_subdims.map((s) => (
                    <span key={s.child_id} className="text-[11px] rounded-sm border border-purple-500/40 bg-purple-50 px-2 py-0.5 font-mono">
                      <b>{s.parent_name}</b> → {s.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div>Batch ID: <span className="font-mono text-xs">{result.batch_id}</span></div>
          </div>
          <Button onClick={reset} className="mt-4" data-testid="mm-reset-btn">Import another file</Button>
        </Card>
      )}
    </div>
  );
}
